/**
 * Confirmation d'une annulation, avec saisie du motif.
 *
 * `components/README.md` attend ce fichier ici. Il vivait dans
 * `pages/BoardPage.tsx`, seul ecran qui annulait ; la cloche des notifications
 * ecarte desormais les commandes d'une table abandonnee, ce qui est la meme
 * annulation vue depuis la table plutot que depuis la carte. Deux dialogues
 * pour une meme action divergeraient.
 *
 * Le motif est obligatoire pour une annulation ordinaire. Il est facultatif
 * pour une table abandonnee : sans lui, l'API pose
 * ABANDONED_SESSION_CANCEL_REASON.
 */

import { useId, useState } from 'react';
import { MAX_CANCEL_REASON_LENGTH } from '@scan2eat/shared';

import { labels } from '../labels.js';
import { Icon } from './Icon.js';
import { Modal } from './Modal.js';

interface CancelDialogProps {
  /** Par defaut, le titre de l'annulation d'une commande. */
  title?: string;
  /** Phrase d'explication placee au-dessus du champ. */
  description?: string;
  reasonLabel?: string;
  /** Precision sous le champ, utile quand le motif est facultatif. */
  reasonHint?: string;
  /** Faux : le champ peut rester vide, l'appelant decide du motif par defaut. */
  reasonRequired?: boolean;
  confirmLabel?: string;
  abortLabel?: string;
  /** Requete en cours : les deux boutons deviennent inertes. */
  pending?: boolean;
  /** Echec de la derniere tentative. Le dialogue reste ouvert pour le montrer. */
  error?: string | null;
  /** Recoit le motif saisi, deja trime. Chaine vide si aucun n'est exige. */
  onConfirm: (note: string) => void;
  onCancel: () => void;
}

export function CancelDialog({
  title = labels.cancel.title,
  description = labels.cancel.description,
  reasonLabel = labels.cancel.reasonLabel,
  reasonHint,
  reasonRequired = true,
  confirmLabel = labels.cancel.confirm,
  abortLabel = labels.cancel.abort,
  pending = false,
  error = null,
  onConfirm,
  onCancel,
}: CancelDialogProps) {
  const [note, setNote] = useState('');
  const [touched, setTouched] = useState(false);
  const fieldId = useId();

  const missing = reasonRequired && !note.trim();

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (missing || pending) return;
    onConfirm(note.trim());
  };

  return (
    <Modal
      title={title}
      description={description}
      onClose={onCancel}
      locked={pending}
      onSubmit={submit}
      footer={
        <>
          <button type="button" onClick={onCancel} disabled={pending} className="btn-ghost">{abortLabel}</button>
          <button type="submit" disabled={pending} className="btn-danger btn-danger--solid">{confirmLabel}</button>
        </>
      }
    >
      <div className="form-grid">
        {error && (
          <p role="alert" className="notice notice--danger" style={{ margin: 0 }}>
            <Icon name="alert" size={18} />
            {error}
          </p>
        )}
        <div className="field">
          <label htmlFor={fieldId} className="field-label">{reasonLabel}</label>
          <textarea
            id={fieldId}
            className="input"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onBlur={() => setTouched(true)}
            required={reasonRequired}
            maxLength={MAX_CANCEL_REASON_LENGTH}
            aria-invalid={touched && missing}
            autoFocus
          />
          {reasonHint && <p className="field-hint">{reasonHint}</p>}
          {touched && missing && <p role="alert" className="field-error">{labels.cancel.reasonRequired}</p>}
        </div>
      </div>
    </Modal>
  );
}
